import React from 'react';
import { getLocalDateInputValue } from '../../../lib/productionLabelUtils';

const NumberField = ({ label, value, onChange, error, readOnly }) => (
  <div className="flex flex-col gap-1 print:gap-0.5">
    <label className="text-xs font-medium text-gray-500">{label}</label>
    <input
      type="number"
      min={0}
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      className={`px-3 py-2 text-sm border rounded-lg bg-white text-gray-900
        focus:outline-none focus:ring-2 focus:ring-primary transition-colors
        print:py-1 print:px-2 print:text-xs
        ${error ? 'border-red-400' : 'border-gray-200'}
        ${readOnly ? 'opacity-80 cursor-default' : ''}`}
    />
    {error && <span className="text-xs text-red-500 print:hidden">{error}</span>}
  </div>
);

const TextField = ({ label, value, onChange, multiline = false, readOnly }) => (
  <div className="flex flex-col gap-1 print:gap-0.5">
    <label className="text-xs font-medium text-gray-500">{label}</label>
    {multiline ? (
      <textarea
        value={value}
        onChange={onChange}
        rows={3}
        readOnly={readOnly}
        placeholder="Description..."
        className={`px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-900
          focus:outline-none focus:ring-2 focus:ring-primary resize-none transition-colors
          print:rows-2 print:py-1 print:px-2 print:text-xs print:min-h-0 print:h-12
          ${readOnly ? 'opacity-80 cursor-default' : ''}`}
      />
    ) : (
      <input
        type="text"
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        className={`px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-900
          focus:outline-none focus:ring-2 focus:ring-primary transition-colors
          print:py-1 print:px-2 print:text-xs
          ${readOnly ? 'opacity-80 cursor-default' : ''}`}
      />
    )}
  </div>
);

/**
 * Corps éditable / lecture seule de la fiche production (modèle, étiquettes…).
 */
export default function ProductionSheetBody({ form, setField, errors = {}, readOnly = false }) {
  return (
    <div className="production-sheet-body p-4 md:p-5 space-y-7 print:p-2 print:space-y-3">
      <section className="production-sheet-section">
        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-teal-500/30 print:mb-2 print:pb-1">
          <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-widest">
            Modèle / Plaquage
          </h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 print:gap-y-2 print:gap-x-4">
          <div className="space-y-3 print:space-y-1">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">UP</p>
            <div className="grid grid-cols-2 gap-3 print:gap-2">
              <NumberField
                label="De"
                value={form.model_up_from}
                onChange={setField('model_up_from')}
                error={errors.model_up_from}
                readOnly={readOnly}
              />
              <NumberField
                label="À"
                value={form.model_up_to}
                onChange={setField('model_up_to')}
                error={errors.model_up_to}
                readOnly={readOnly}
              />
            </div>
          </div>
          <div className="space-y-3 print:space-y-1">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">LOW</p>
            <div className="grid grid-cols-2 gap-3 print:gap-2">
              <NumberField
                label="De"
                value={form.model_low_from}
                onChange={setField('model_low_from')}
                error={errors.model_low_from}
                readOnly={readOnly}
              />
              <NumberField
                label="À"
                value={form.model_low_to}
                onChange={setField('model_low_to')}
                error={errors.model_low_to}
                readOnly={readOnly}
              />
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-3 print:mt-2 print:space-y-1.5">
          <TextField
            label="Élastique"
            value={form.elastic}
            onChange={setField('elastic')}
            readOnly={readOnly}
          />
          <TextField
            label="Description / Confirmation ALI"
            value={form.ali_confirmation}
            onChange={setField('ali_confirmation')}
            multiline
            readOnly={readOnly}
          />
          <TextField
            label="Suppression"
            value={form.suppression}
            onChange={setField('suppression')}
            readOnly={readOnly}
          />
        </div>
      </section>

      <section className="production-sheet-section">
        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-teal-500/30 print:mb-2 print:pb-1">
          <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-widest">
            Étiquette
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 print:gap-2">
          <NumberField
            label="De"
            value={form.label_from}
            onChange={setField('label_from')}
            error={errors.label_from}
            readOnly={readOnly}
          />
          <NumberField
            label="À"
            value={form.label_to}
            onChange={setField('label_to')}
            error={errors.label_to}
            readOnly={readOnly}
          />
        </div>
        <div className="mt-4 max-w-xs print:mt-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">
              Date d&apos;emballage (jour d&apos;impression des étiquettes)
            </label>
            <input
              type="date"
              value={form.packaging_date || ''}
              onChange={setField('packaging_date')}
              onFocus={() => {
                if (!readOnly && !form.packaging_date) {
                  setField('packaging_date')(getLocalDateInputValue());
                }
              }}
              readOnly={readOnly}
              className={`px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-900
                focus:outline-none focus:ring-2 focus:ring-primary transition-colors
                print:py-1 print:text-xs
                ${readOnly ? 'opacity-80 cursor-default' : ''}`}
            />
            <p className="text-xs text-gray-500 print:hidden">
              Cette date correspond à l’emballage réel, pas à la date de production.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

/** Ancien défaut UI (jamais une vraie OF métier). */
export const LEGACY_OF_PLACEHOLDER = 45;

/** Plage modèle / plaquage par défaut (avant init conception). */
export const DEFAULT_MODEL_RANGE_TO = 12;

/**
 * Moitié des aligneurs conception → valeur « À » pour UP et LOW.
 * Ex. 24 → 12 ; 27 → 13.
 */
export function splitAlignersPerArch(totalAligners) {
  const n = Number(totalAligners);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n / 2);
}

function isDefaultModelRange(from, to) {
  const f = from == null ? 0 : Number(from);
  const t = to == null ? DEFAULT_MODEL_RANGE_TO : Number(to);
  return f === 0 && t === DEFAULT_MODEL_RANGE_TO;
}

/**
 * Total aligneurs affiché en en-tête = UP À + LOW À.
 */
export function computeAlignersTotalFromRanges(source) {
  if (!source) return null;
  const up = Number(source.model_up_to ?? 0);
  const low = Number(source.model_low_to ?? 0);
  if (Number.isNaN(up) || Number.isNaN(low)) return null;
  return up + low;
}

/**
 * N° OF (colonne legacy) : valeur stockée uniquement.
 * Efface l’ancien placeholder 45 tant que la fiche n’est pas packagée.
 * L’UI n’édite plus ce champ ; le total aligneurs vient de UP À + LOW À.
 */
export function resolveProductionOfNumber(storedOf, _conceptionAligners, { packagingDate = null } = {}) {
  if (storedOf != null && storedOf !== '') {
    const n = Number(storedOf);
    if (!Number.isNaN(n)) {
      if (!packagingDate && n === LEGACY_OF_PLACEHOLDER) return '';
      return n;
    }
  }
  return '';
}

/**
 * Initialise UP/LOW « À » avec la moitié des aligneurs conception si plages encore par défaut.
 */
export function resolveModelRangesFromConception(batch, conceptionAligners) {
  const half = splitAlignersPerArch(conceptionAligners);
  const packagingDate = batch?.packaging_date || null;
  const upFrom = batch?.model_up_from ?? 0;
  const upTo = batch?.model_up_to ?? DEFAULT_MODEL_RANGE_TO;
  const lowFrom = batch?.model_low_from ?? 0;
  const lowTo = batch?.model_low_to ?? DEFAULT_MODEL_RANGE_TO;

  const canInit =
    half != null &&
    !packagingDate &&
    isDefaultModelRange(batch?.model_up_from, batch?.model_up_to) &&
    isDefaultModelRange(batch?.model_low_from, batch?.model_low_to);

  if (canInit) {
    return {
      model_up_from: 0,
      model_up_to: half,
      model_low_from: 0,
      model_low_to: half,
    };
  }

  return {
    model_up_from: upFrom,
    model_up_to: upTo,
    model_low_from: lowFrom,
    model_low_to: lowTo,
  };
}

export function emptyProductionForm() {
  return {
    model_up_from: 0,
    model_up_to: DEFAULT_MODEL_RANGE_TO,
    model_low_from: 0,
    model_low_to: DEFAULT_MODEL_RANGE_TO,
    elastic: '',
    ali_confirmation: '',
    suppression: '',
    label_from: 0,
    label_to: DEFAULT_MODEL_RANGE_TO,
    of_number: '',
    packaging_date: '',
  };
}

/**
 * @param {object|null} batch
 * @param {number|string|null} [conceptionAligners] — total aligneurs phase conception
 */
export function formFromBatch(batch, conceptionAligners = '') {
  if (!batch) return emptyProductionForm();
  const packaging_date = batch.packaging_date?.slice?.(0, 10) || '';
  const ranges = resolveModelRangesFromConception(batch, conceptionAligners);
  return {
    ...ranges,
    elastic: batch.elastic || '',
    ali_confirmation: batch.ali_confirmation || '',
    suppression: batch.suppression || '',
    label_from: batch.label_from ?? 0,
    label_to: batch.label_to ?? DEFAULT_MODEL_RANGE_TO,
    of_number: resolveProductionOfNumber(batch.of_number, null, {
      packagingDate: packaging_date || batch.packaging_date || null,
    }),
    packaging_date,
  };
}

export function batchUpdatePayload(form) {
  const alignersTotal = computeAlignersTotalFromRanges(form);
  return {
    model_up_from: Number(form.model_up_from),
    model_up_to: Number(form.model_up_to),
    model_low_from: Number(form.model_low_from),
    model_low_to: Number(form.model_low_to),
    elastic: form.elastic,
    ali_confirmation: form.ali_confirmation,
    suppression: form.suppression,
    label_from: Number(form.label_from),
    label_to: Number(form.label_to),
    // Colonne legacy : synchronisée avec UP À + LOW À
    of_number: alignersTotal != null ? alignersTotal : null,
    packaging_date: form.packaging_date || null,
  };
}

export function validateProductionForm(form) {
  const errs = {};
  ['model_up_from', 'model_up_to', 'model_low_from', 'model_low_to', 'label_from', 'label_to'].forEach(
    (f) => {
      if (Number(form[f]) < 0) errs[f] = 'Doit être ≥ 0';
    }
  );
  return errs;
}
